import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import './Organisation.css';

const Organisation = () => {
  const [organisation, setOrganisation] = useState(null);
  // eslint-disable-next-line no-unused-vars -- setter used for future connection status UI
  const [connectionStatus, setConnectionStatus] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    contactName: '',
    email: ''
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchOrganisationData = async () => {
      try {
        const response = await axios.get(createApiUrl(`/api/organisation/${id}`));
        setOrganisation(response.data);
        // Initialize edit form with current values
        setEditForm({
          contactName: response.data.contactName || '',
          email: response.data.email || ''
        });
      } catch (error) {
        console.error('Error fetching organisation data:', error);
        setConnectionStatus('Error connecting to server');
      }
    };

    const checkAdminStatus = async () => {
      try {
        const response = await axios.get(createApiUrl('/api/dashboard'), { withCredentials: true });
        
        // Check for both isAdmin and role === 'admin'
        if (response.data && response.data.user && (response.data.user.isAdmin || response.data.user.role === 'admin')) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    fetchOrganisationData();
    checkAdminStatus();
    checkConnection();
  }, [id]);

  const checkConnection = () => {
    if (!navigator.onLine) {
      setConnectionStatus('You are offline');
    } else {
      setConnectionStatus('');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    setEditForm({
      contactName: organisation.contactName || '',
      email: organisation.email || ''
    });
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(createApiUrl(`/api/organisation/${id}`), {
        contactName: editForm.contactName,
        email: editForm.email
      }, { withCredentials: true });
      
      setOrganisation(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating organisation:', error);
      alert('Failed to update organisation');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderSubscriptions = (subscriptions) => {
    if (Array.isArray(subscriptions)) {
      return subscriptions.map((id, index) => (
        <React.Fragment key={index}>
          {id}<br />
        </React.Fragment>
      ));
    } else if (typeof subscriptions === 'string') {
      return subscriptions.split(',').map((id, index) => (
        <React.Fragment key={index}>
          {id}<br />
        </React.Fragment>
      ));
    }
    return 'No subscriptions available';
  };

  if (!organisation) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="organisation-detail-container">
      <div className="container">
        <div className="organisation-detail-main-content">
          <div className="organisation-detail-header">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="page-title">Organisation Details</h2>
              <button 
                onClick={() => navigate('/admin-dashboard')} 
                className="btn-back"
              >
                Back to Admin Dashboard
              </button>
            </div>
          </div>

          {organisation && (
            <>
              <Row>
                <Col md={6}>
                  <Card className="organisation-detail-card mb-4">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h4>Organisation Information</h4>
                        {isAdmin && (
                          <div>
                            {!isEditing ? (
                              <button 
                                onClick={handleEdit}
                                className="btn btn-primary btn-sm"
                              >
                                Edit
                              </button>
                            ) : (
                              <div>
                                <button 
                                  onClick={handleSave}
                                  className="btn btn-success btn-sm me-2"
                                >
                                  Save
                                </button>
                                <button 
                                  onClick={handleCancel}
                                  className="btn btn-secondary btn-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Name:</strong>
                            <p>{organisation.name}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Code:</strong>
                            <p>{organisation.code}</p>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Contact:</strong>
                            {isEditing ? (
                              <input
                                type="text"
                                name="contactName"
                                value={editForm.contactName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            ) : (
                              <p>{organisation.contactName}</p>
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Email:</strong>
                            {isEditing ? (
                              <input
                                type="email"
                                name="email"
                                value={editForm.email}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            ) : (
                              <p>{organisation.email}</p>
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>ID:</strong>
                            <p>{organisation._id}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Registered:</strong>
                            <p>{formatDate(organisation.createdAt)}</p>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="organisation-detail-card mb-4">
                    <Card.Header>
                      <h4>Account Information</h4>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Stripe Customer ID:</strong>
                            <p>{organisation.stripeCustomerId || 'Not set'}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="organisation-detail-info">
                            <strong>Status:</strong>
                            <p><span className={`badge bg-${organisation.isOrgAdmin ? 'success' : 'warning'}`}>
                              {organisation.isOrgAdmin ? 'Active' : 'Inactive'}
                            </span></p>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="organisation-detail-card mb-4">
                <Card.Header>
                  <h4>Subscriptions</h4>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Quantity</th>
                          <th>Remaining</th>
                          <th>Starts</th>
                          <th>Expires</th>
                          <th>Subscriptions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organisation.orgSubscriptions.map((subscription, index) => (
                          <tr
                            key={index}
                            className={
                              subscription.status === 'expired' || 
                              subscription.status === 'cancelled' 
                                ? 'greyed-out' 
                                : ''
                            }
                          >
                            <td>{subscription.subScriptionCode}</td>
                            <td>{subscription.quantity}</td>
                            <td>{subscription.remainingQuantity}</td>
                            <td>{formatDate(subscription.startDate)}</td>
                            <td>{formatDate(subscription.endDate)}</td>
                            <td>{renderSubscriptions(subscription.subscriptions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>

              <Card className="organisation-detail-card mb-4">
                <Card.Header>
                  <h4>Associated Schools</h4>
                </Card.Header>
                <Card.Body>
                  {organisation.associatedSchools && organisation.associatedSchools.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>School Name</th>
                            <th>School Code</th>
                            <th>Teacher Code</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Subscription ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organisation.associatedSchools.map((school, index) => (
                            <tr key={school._id || index}>
                              <td>{school.schoolName || 'N/A'}</td>
                              <td>{school.schoolCode || 'N/A'}</td>
                              <td>{school.tchCode || 'N/A'}</td>
                              <td>{school.contactName || 'N/A'}</td>
                              <td>{school.email || 'N/A'}</td>
                              <td>
                                {school.subscriptionIds && school.subscriptionIds.length > 0 ? (
                                  school.subscriptionIds.map((id, idx) => (
                                    <span key={idx}>
                                      {id}
                                      {idx < school.subscriptionIds.length - 1 && <br />}
                                    </span>
                                  ))
                                ) : (
                                  'N/A'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No associated schools found.</p>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Organisation; 